import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import { AppLayout } from '@/components/layouts/AppLayout'
import { ChannelSidebar, ChannelSidebarItem } from '@/components/messages/ChannelSidebar'
import { MessageThread, ChannelDetail, MessageData } from '@/components/messages/MessageThread'
import { useChannelMessages } from '@/hooks/useChannelMessages'
import { useAllChannelUpdates } from '@/hooks/useAllChannelUpdates'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface Props {
  channels: ChannelSidebarItem[]
  selected_channel: ChannelDetail | null
  messages: MessageData[]
  available_agents?: AgentRef[]
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') ?? ''
}

function markChannelRead(channelSlug: string) {
  fetch(`/messages/channels/${channelSlug}/mark_read`, {
    method: 'POST',
    headers: { 'X-CSRF-Token': getCsrfToken() },
  }).catch(() => {})
}

export default function Index({ channels: initialChannels, selected_channel, messages: initialMessages, available_agents }: Props) {
  const [channels, setChannels] = useState(initialChannels)
  const [messages, setMessages] = useState<MessageData[]>(initialMessages)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Reset state when Inertia navigation updates props
  useEffect(() => {
    setMessages(initialMessages)
  }, [selected_channel?.id, initialMessages])

  useEffect(() => {
    setChannels(initialChannels)
  }, [initialChannels])

  // Close sidebar on mobile when a channel is selected
  useEffect(() => {
    if (isMobile && selected_channel) {
      setSidebarOpen(false)
    }
  }, [selected_channel?.id, isMobile])

  // Real-time messages for the selected channel
  const handleNewMessage = useCallback((message: MessageData) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev
      return [...prev, message]
    })
    // Auto mark as read since we're viewing this channel
    if (selected_channel) {
      markChannelRead(selected_channel.slug)
      // Reset unread for this channel in sidebar
      setChannels((prev) =>
        prev.map((ch) =>
          ch.id === selected_channel.id ? { ...ch, unread_count: 0 } : ch
        )
      )
    }
  }, [selected_channel?.id, selected_channel?.slug])

  const handleMessageDeleted = useCallback((messageId: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId))
  }, [])

  useChannelMessages(
    selected_channel?.id ?? null,
    handleNewMessage,
    handleMessageDeleted
  )

  // Real-time unread updates for non-selected channels
  const handleOtherChannelMessage = useCallback((channelId: number) => {
    setChannels((prev) =>
      prev.map((ch) =>
        ch.id === channelId ? { ...ch, unread_count: ch.unread_count + 1 } : ch
      )
    )
  }, [])

  useAllChannelUpdates(
    channels.map((ch) => ch.id),
    selected_channel?.id ?? null,
    handleOtherChannelMessage
  )

  const sidebarContent = (
    <ChannelSidebar
      channels={channels}
      selectedSlug={selected_channel?.slug ?? null}
      availableAgents={available_agents}
    />
  )

  return (
    <AppLayout noPadding>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Desktop sidebar */}
        {!isMobile && sidebarContent}

        {/* Mobile sidebar drawer */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{ sx: { width: 280 } }}
          >
            {sidebarContent}
          </Drawer>
        )}

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {selected_channel ? (
            <MessageThread
              channel={selected_channel}
              messages={messages}
              onMenuClick={isMobile ? () => setSidebarOpen(true) : undefined}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                gap: 1,
              }}
            >
              {isMobile && (
                <IconButton onClick={() => setSidebarOpen(true)} sx={{ mb: 1, minWidth: 44, minHeight: 44 }}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="body2" color="text.secondary">
                Select a channel or start a direct message
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </AppLayout>
  )
}
